/**
 * Quick-view drawers de la Vista 360 (Fase 3).
 * Cada componente se renderiza dentro de un QuickViewDrawer con header propio,
 * acciones contextuales (gateadas por permisos) y body con datos reales.
 *
 * - ProductQuickView   precio/stock/últimas ventas (GET /products/<id>/context/)
 * - CustomerQuickView   situación financiera + últimos docs (navegan en el 360)
 * - VoucherQuickView    comprobante + Aprobar/Reversar (gateado, sin salir)
 * - PaymentQuickView    pago + Confirmar/Anular (gateado)
 */
import { todayISO } from '../../../../../lib/utils'
import { useState } from 'react'
import { DataTable } from '@/components/ds/DataTable'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  User, Package, BookOpen, DollarSign, Loader2, CheckCircle2, RotateCcw, XCircle, Pencil,
} from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { fmtDate, formatRut } from '@/lib/utils'
import { useTenantCurrency } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { useApproveVoucher, useReverseVoucher } from '@/modules/accounting/hooks/useAccounting'
import { VoucherForm } from '@/modules/accounting/components/VoucherForm'
import { useConfirmPayment, useCancelPayment, useCustomer, salesKeys } from '@/modules/sales/hooks/useSales'
import { QuickViewDrawer } from '@/components/three60'
import { CustomerContextTab } from '../tabs/CustomerContextTab'
import { useDocumentVoucher, type CollectionResponse } from '../tabs/use360Queries'
import { useTenantTax } from '@/modules/config/hooks/useConfig'

type Fmt = (v: string | number) => string
export interface QuickNotify {
  success: (m: string) => void
  error: (m: string) => void
}

function DrawerBtn({ label, icon, variant = 'default', onClick, busy }: {
  label: string; icon?: React.ReactNode; variant?: 'default' | 'primary' | 'danger'; onClick: () => void; busy?: boolean
}) {
  const cls = {
    default: 'bg-surface border-divider-strong text-content hover:bg-surface-muted',
    primary: 'bg-primary border-primary text-on-primary hover:bg-primary-hover',
    danger: 'bg-surface border-danger/40 text-danger hover:bg-danger/10',
  }[variant]
  return (
    <button onClick={onClick} disabled={busy} className={`rounded-lg px-3 py-2 text-[12.5px] font-semibold inline-flex items-center gap-1.5 border ${cls} disabled:opacity-60`}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : icon}{label}
    </button>
  )
}

// ─── Producto ──────────────────────────────────────────────────────────────────

interface ProductContextResponse {
  id: string; sku: string; name: string; item_type: string; is_tax_exempt: boolean
  prices: Array<{ currency: string; unit_price: string; is_default: boolean }>
  stock: Array<{ warehouse: string | null; quantity: string; reserved_quantity: string; available_quantity: string }>
  recent_sales: Array<{ document_id: string; document_type: string; folio: string | null; issue_date: string | null; quantity: string | null; unit_price: string | null }>
}

export function ProductQuickView({ productId, productName, onClose, fmt, locale, onNavigate }: {
  productId: string; productName: string; onClose: () => void; fmt: Fmt; locale: string; onNavigate: (docId: string) => void
}) {
  // La moneda «propia» del producto es la BASE del tenant, no CLP: en un tenant
  // peruano un precio en soles no es un precio en moneda extranjera.
  const baseCurrency = useTenantCurrency()
  const tax = useTenantTax()
  const { data, isLoading } = useQuery<ProductContextResponse>({
    queryKey: ['doc360', 'product-context', productId],
    queryFn: async () => (await apiClient.get(`/sales/products/${productId}/context/`)).data,
  })
  return (
    <QuickViewDrawer open onClose={onClose} icon={<Package size={19} />} title={productName} subtitle={data?.sku}>
      {isLoading || !data ? (
        <div className="grid place-items-center py-10 text-content-muted"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="space-y-4 text-[13px]">
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mb-2">Precios</p>
            {data.prices.map((p) => (
              <div key={p.currency} className="flex justify-between py-1">
                <span className="text-content-muted">{p.currency}{p.is_default ? ' (default)' : ''}</span>
                <span className="font-semibold tabular-nums">{p.currency === baseCurrency ? fmt(p.unit_price) : `${p.currency} ${Number(p.unit_price).toLocaleString(locale)}`}</span>
              </div>
            ))}
            <div className="flex justify-between py-1">
              <span className="text-content-muted">Impuesto</span>
              <span className="font-semibold">{data.is_tax_exempt ? 'Exento' : `Afecto a ${tax.name}`}</span>
            </div>
          </div>

          {data.stock.length > 0 && (
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mb-2">Stock por bodega</p>
              {data.stock.map((s, i) => (
                <div key={i} className="flex justify-between py-1">
                  <span className="text-content-muted">{s.warehouse || 'Bodega'}</span>
                  <span className="font-semibold tabular-nums">{Number(s.available_quantity)} disp.</span>
                </div>
              ))}
            </div>
          )}

          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mb-2">Últimas ventas</p>
            {data.recent_sales.length === 0 ? (
              <p className="text-content-subtle">Sin ventas recientes.</p>
            ) : (
              data.recent_sales.map((s) => (
                <button key={s.document_id} onClick={() => { onClose(); onNavigate(s.document_id) }} className="flex justify-between w-full py-1.5 border-b border-divider last:border-0 hover:text-primary">
                  <span>{s.document_type} {s.folio || '—'} · {s.issue_date ? fmtDate(s.issue_date, locale) : ''}</span>
                  <span className="tabular-nums">{s.quantity} u.</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </QuickViewDrawer>
  )
}

// ─── Cliente ───────────────────────────────────────────────────────────────────

export function CustomerQuickView({ customerId, onClose, fmt, locale, onNavigate }: {
  customerId: string; onClose: () => void; fmt: Fmt; locale: string; onNavigate: (docId: string) => void
}) {
  const { data: c } = useCustomer(customerId)
  const term = c?.payment_term_days ? `Crédito ${c.payment_term_days} días` : 'Contado'
  return (
    <QuickViewDrawer
      open onClose={onClose} icon={<User size={19} />}
      title={c?.legal_name || 'Cliente'}
      subtitle={c ? formatRut(c.tax_id) : undefined}
    >
      {/* Ficha del cliente: datos maestros + situación financiera (in-place, sin
          salir del 360 — igual que openQuick('customer') del mockup). */}
      <div className="text-[13px]">
        <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mb-2">Datos</p>
        <KvLine k="Giro" v={c?.giro || '—'} />
        <KvLine k="Dirección" v={[c?.address_street, c?.address_commune, c?.address_city].filter(Boolean).join(', ') || '—'} />
        <KvLine k="Contacto" v={c?.contact_name || '—'} />
        <KvLine k="Email" v={c?.email_contact || '—'} />
        <KvLine k="Teléfono" v={c?.phone || '—'} />
        <KvLine k="Condición de pago" v={term} />
        <KvLine k="Límite de crédito" v={c?.credit_limit ? fmt(c.credit_limit) : 'Sin límite'} />
      </div>
      <div className="mt-4 pt-4 border-t border-divider">
        <CustomerContextTab customerId={customerId} fmt={fmt} locale={locale} onNavigate={(id) => { onClose(); onNavigate(id) }} />
      </div>
    </QuickViewDrawer>
  )
}

function KvLine({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-content-muted">{k}</span>
      <span className="font-semibold text-right">{v}</span>
    </div>
  )
}

// ─── Comprobante contable ───────────────────────────────────────────────────────

export function VoucherQuickView({ docId, onClose, fmt, locale, notify }: {
  docId: string; onClose: () => void; fmt: Fmt; locale: string; notify: QuickNotify
}) {
  const qc = useQueryClient()
  const { can } = usePermission()
  const canApprove = can('accounting.vouchers', 'approve')
  const canEdit = can('accounting.vouchers', 'edit')
  const canEditApproved = can('accounting.vouchers', 'edit_approved')
  const { data, isLoading } = useDocumentVoucher(docId, true)
  const approve = useApproveVoucher()
  const reverse = useReverseVoucher()
  const { confirm, modalProps } = useConfirmModal()
  const [reverseOpen, setReverseOpen] = useState(false)
  const [revDate, setRevDate] = useState(todayISO())
  const [revDesc, setRevDesc] = useState('')
  const [editOpen, setEditOpen] = useState(false)

  const v = data?.voucher
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['doc360'] })
    qc.invalidateQueries({ queryKey: salesKeys.document(docId) })
    qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
  }
  const canEditThis = !!v && ((v.status === 'draft' && canEdit) || (v.status === 'approved' && canEditApproved))
  const openEdit = () => {
    if (!v) return
    if (v.status === 'approved') {
      confirm({
        variant: 'danger',
        confirmLabel: 'Editar de todas formas',
        message:
          'Se va a EDITAR un comprobante ya APROBADO. Esto puede descuadrar la contabilidad y ' +
          'afectar balances, EERR, F22/F29 y reportes ya emitidos. El comprobante permanece ' +
          'aprobado y el cambio queda registrado en la bitácora. ¿Continuar?',
        onConfirm: () => setEditOpen(true),
      })
    } else {
      setEditOpen(true)
    }
  }

  const doApprove = () => {
    if (!v) return
    approve.mutate(v.id, {
      onSuccess: () => { notify.success(`Comprobante ${v.number} aprobado.`); refresh(); onClose() },
      onError: () => notify.error('No se pudo aprobar el comprobante.'),
    })
  }
  const doReverse = () => {
    if (!v) return
    reverse.mutate({ id: v.id, date: revDate, description: revDesc || undefined }, {
      onSuccess: () => { notify.success(`Comprobante ${v.number} reversado.`); refresh(); onClose() },
      onError: () => notify.error('No se pudo reversar (¿período cerrado?).'),
    })
  }

  const showApprove = !!v && canApprove && v.status === 'draft'
  const showReverse = !!v && canApprove && v.status === 'approved'
  const actions = (canEditThis || showApprove || showReverse) ? (
    <>
      {canEditThis && <DrawerBtn label="Editar" icon={<Pencil size={14} />} onClick={openEdit} />}
      {showApprove && <DrawerBtn label="Aprobar" variant="primary" icon={<CheckCircle2 size={14} />} onClick={doApprove} busy={approve.isPending} />}
      {showReverse && <DrawerBtn label="Reversar" variant="danger" icon={<RotateCcw size={14} />} onClick={() => setReverseOpen((o) => !o)} />}
    </>
  ) : undefined

  return (
    <>
    <QuickViewDrawer open onClose={onClose} icon={<BookOpen size={19} />} title={v?.number || 'Comprobante'} subtitle={v?.status_display} actions={actions}>
      {isLoading ? (
        <div className="grid place-items-center py-10 text-content-muted"><Loader2 className="animate-spin" /></div>
      ) : !v ? (
        <p className="text-content-subtle text-center py-10 text-[13px]">Este documento aún no tiene comprobante contable.</p>
      ) : (
        <div className="text-[13px]">
          <div className="flex justify-between py-1"><span className="text-content-muted">Fecha</span><span className="font-semibold">{v.date ? fmtDate(v.date, locale) : '—'}</span></div>
          {v.description && <p className="text-content-muted text-[12px] my-2">{v.description}</p>}

          {reverseOpen && (
            <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 my-3 space-y-2">
              <p className="text-[12px] font-semibold text-danger">Reversar comprobante</p>
              <label className="block text-[11.5px] text-content-muted">Fecha de reversa
                <input type="date" value={revDate} onChange={(e) => setRevDate(e.target.value)} className="mt-1 w-full rounded-lg border border-input-border bg-input-bg px-2.5 py-1.5 text-[13px]" />
              </label>
              <label className="block text-[11.5px] text-content-muted">Descripción (opcional)
                <input value={revDesc} onChange={(e) => setRevDesc(e.target.value)} placeholder="Motivo de la reversa" className="mt-1 w-full rounded-lg border border-input-border bg-input-bg px-2.5 py-1.5 text-[13px]" />
              </label>
              <DrawerBtn label="Confirmar reversa" variant="danger" icon={<RotateCcw size={14} />} onClick={doReverse} busy={reverse.isPending} />
            </div>
          )}

          <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mt-3 mb-2">Líneas</p>
          <DataTable<typeof v.lines[number]>
            columns={[
              { key: 'account', header: 'Cuenta',
                render: ln => (
                  <>{ln.account_code ? `${ln.account_code} · ` : ''}{ln.account_name || ln.glosa}</>
                ) },
              { key: 'debit', header: 'Debe', numeric: true,
                render: ln => (Number(ln.debit) ? fmt(ln.debit) : '') },
              { key: 'credit', header: 'Haber', numeric: true,
                render: ln => (Number(ln.credit) ? fmt(ln.credit) : '') },
            ]}
            rows={v.lines}
            rowKey={(_, i) => i}
            minWidth={320}
            surface={false}
            caption="Líneas del asiento contable"
          />
        </div>
      )}
    </QuickViewDrawer>
    {editOpen && v && (
      <VoucherForm
        voucher={{ id: v.id }}
        onClose={() => setEditOpen(false)}
        onSaved={() => { notify.success(`Comprobante ${v.number} actualizado.`); refresh() }}
      />
    )}
    <ConfirmModal {...modalProps} />
    </>
  )
}

// ─── Pago ────────────────────────────────────────────────────────────────────

export function PaymentQuickView({ payment, onClose, fmt, locale, notify }: {
  payment: CollectionResponse['payments'][number]; onClose: () => void; fmt: Fmt; locale: string; notify: QuickNotify
}) {
  const qc = useQueryClient()
  const { can } = usePermission()
  const canEdit = can('sales.collections', 'edit')
  const confirmPay = useConfirmPayment()
  const cancelPay = useCancelPayment()

  const refresh = () => qc.invalidateQueries({ queryKey: ['doc360'] })

  const doConfirm = () => confirmPay.mutate(payment.payment_id, {
    onSuccess: () => { notify.success('Pago confirmado.'); refresh(); onClose() },
    onError: () => notify.error('No se pudo confirmar el pago.'),
  })
  const doCancel = () => cancelPay.mutate(payment.payment_id, {
    onSuccess: () => { notify.success('Pago anulado.'); refresh(); onClose() },
    onError: () => notify.error('No se pudo anular el pago.'),
  })

  const actions = canEdit && payment.status === 'registered' ? (
    <>
      <DrawerBtn label="Confirmar" variant="primary" icon={<CheckCircle2 size={14} />} onClick={doConfirm} busy={confirmPay.isPending} />
      <DrawerBtn label="Anular" variant="danger" icon={<XCircle size={14} />} onClick={doCancel} busy={cancelPay.isPending} />
    </>
  ) : undefined

  return (
    <QuickViewDrawer open onClose={onClose} icon={<DollarSign size={19} />} title={fmt(payment.amount)} subtitle={payment.status_display} actions={actions}>
      <div className="text-[13px] space-y-1">
        <div className="flex justify-between py-1"><span className="text-content-muted">Fecha</span><span className="font-semibold">{payment.payment_date ? fmtDate(payment.payment_date, locale) : '—'}</span></div>
        <div className="flex justify-between py-1"><span className="text-content-muted">Método</span><span className="font-semibold">{payment.payment_method_display}</span></div>
        {payment.bank_account && <div className="flex justify-between py-1"><span className="text-content-muted">Banco</span><span className="font-semibold">{payment.bank_account}</span></div>}
        {payment.reference && <div className="flex justify-between py-1"><span className="text-content-muted">Referencia</span><span className="font-semibold">{payment.reference}</span></div>}
      </div>
    </QuickViewDrawer>
  )
}
